import type { ApiClient } from "./client";
import type { PhotoMetadata, PhotoSide, PhotoUpload, PhotoUploadRequest } from "./evidence";
import type { PhotoContentType } from "./photo-types";
import type { PhotoSlot } from "./services";
import { toAppointmentWorkflows, type SlotPhotoRow } from "./slot-photos";

type PhotosApi = Pick<ApiClient["appointments"], "photos" | "photoSlots" | "recordPhoto" | "photoUpload">;

export type AppointmentPhotoPurpose = Exclude<PhotoUploadRequest["purpose"], PhotoSide>;

export type PhotoFiles<File> = {
  contentTypeOf: (file: File) => PhotoContentType;
  put: (upload: PhotoUpload, file: File, contentType: PhotoContentType) => Promise<void>;
};

export type SlotPhotoFile<File> = { workflow: string; isAfter: boolean; file: File; metadata: PhotoMetadata };

export function createAppointmentPhotos<File>(api: PhotosApi, { contentTypeOf, put }: PhotoFiles<File>) {
  const uploaded = async (appointmentId: string, request: PhotoUploadRequest, file: File): Promise<string> => {
    const upload = await api.photoUpload(appointmentId, request);
    await put(upload, file, request.contentType);
    return upload.photoUrl;
  };

  return {
    read: async (appointmentId: string): Promise<SlotPhotoRow[]> => toAppointmentWorkflows(appointmentId, await api.photos(appointmentId)),

    slots: async (appointmentId: string): Promise<PhotoSlot[]> => (await api.photoSlots(appointmentId)).items.map(({ id, title, description }) => ({ id, title, description })),

    upload: (appointmentId: string, purpose: AppointmentPhotoPurpose, file: File): Promise<string> => uploaded(appointmentId, { purpose, contentType: contentTypeOf(file) }, file),

    async recordSlotPhoto(appointmentId: string, { workflow, isAfter, file, metadata }: SlotPhotoFile<File>): Promise<SlotPhotoRow[]> {
      const side = isAfter ? "after" : "before";
      const url = await uploaded(appointmentId, { purpose: side, workflow, contentType: contentTypeOf(file) }, file);
      return toAppointmentWorkflows(appointmentId, await api.recordPhoto(appointmentId, { workflow, side, url, metadata }));
    },
  };
}
